import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, getToken, setToken, clearToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Loader2,
  Lock,
  ArrowLeft,
  LogOut,
  Trash2,
  Phone,
  Mail,
  CalendarDays,
  Inbox,
  Calendar,
  Home as HomeIcon,
  Star,
  Plus,
  Pencil,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const Admin = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    (async () => {
      if (!getToken()) {
        setChecking(false);
        return;
      }
      try {
        await api.verify();
        setAuthed(true);
      } catch {
        clearToken();
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoggingIn(true);
    try {
      const d = await api.login(password);
      setToken(d.token);
      setAuthed(true);
      toast.success("Welcome to Admin Panel");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect password");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    setAuthed(false);
    toast.success("Signed out");
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F3]">
        <Loader2 className="w-6 h-6 animate-spin text-[#C8A97E]" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F3] px-5">
        <Card className="w-full max-w-md p-8 border-[#E5E0D8] shadow-[0_10px_40px_rgba(0,0,0,0.05)]">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-[#FAF3E7] flex items-center justify-center mx-auto mb-4">
              <Lock className="w-5 h-5 text-[#C8A97E]" />
            </div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#C8A97E] font-semibold mb-2">
              Admin Panel
            </div>
            <h1 className="font-serif text-[28px] font-semibold text-[#1A1A1A]">
              Hitha Residency
            </h1>
            <p className="text-[13px] text-[#666] mt-2">
              Enter your owner password to manage bookings
            </p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              type="password"
              placeholder="Owner password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#F9F7F3] border-[#E5E0D8] h-11 focus-visible:border-[#C8A97E] focus-visible:ring-[#C8A97E]/20"
              data-testid="admin-password"
              autoFocus
            />
            <Button
              type="submit"
              disabled={loggingIn || !password}
              className="w-full bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white rounded-full h-11 font-semibold"
              data-testid="admin-login-btn"
            >
              {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
            </Button>
            <Link
              to="/"
              className="block text-center text-[12px] text-[#666] hover:text-[#C8A97E] mt-4"
            >
              ← Back to website
            </Link>
          </form>
        </Card>
      </div>
    );
  }

  return <AdminPanel onLogout={handleLogout} />;
};

const AdminPanel = ({ onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, c, r] = await Promise.all([
        api.listBookings(),
        api.listContacts(),
        api.listReviewsAdmin(),
      ]);
      setBookings(b);
      setContacts(c);
      setReviews(r);
    } catch {
      toast.error("Could not load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.deleteBooking(id);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success("Booking deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.deleteContact(id);
      setContacts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F3]" data-testid="admin-panel">
      {/* Top bar */}
      <header className="bg-white border-b border-[#E5E0D8] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="w-9 h-9 rounded-full bg-[#F9F7F3] hover:bg-[#FAF3E7] flex items-center justify-center transition-colors"
              data-testid="back-to-site"
            >
              <ArrowLeft className="w-4 h-4 text-[#1A1A1A]" />
            </Link>
            <div>
              <div className="font-serif text-[17px] font-semibold text-[#1A1A1A] leading-tight">
                Admin Panel
              </div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[#C8A97E] font-semibold">
                Hitha Residency
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 text-[13px] text-[#666] hover:text-[#1A1A1A]"
            >
              <HomeIcon className="w-4 h-4" /> View Website
            </Link>
            <Button
              onClick={onLogout}
              variant="outline"
              className="rounded-full border-[#E5E0D8] text-[#1A1A1A] hover:bg-[#F9F7F3] h-9"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        <div className="mb-8">
          <h1 className="font-serif text-[34px] font-semibold text-[#1A1A1A] leading-tight mb-1">
            All Enquiries
          </h1>
          <p className="text-[14px] text-[#666]">
            View, manage and respond to guest enquiries and bookings
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatBox icon={Calendar} label="Booking Enquiries" count={bookings.length} />
          <StatBox icon={Inbox} label="Contact Submissions" count={contacts.length} />
          <StatBox icon={Star} label="Reviews" count={reviews.length} />
          <StatBox
            icon={CalendarDays}
            label="Total Leads"
            count={bookings.length + contacts.length}
          />
        </div>

        <Tabs defaultValue="contacts" className="w-full">
          <TabsList className="bg-white border border-[#E5E0D8] rounded-full p-1">
            <TabsTrigger
              value="contacts"
              className="rounded-full data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white px-5 data-[state=active]:shadow-none"
              data-testid="tab-contacts"
            >
              Contact Form ({contacts.length})
            </TabsTrigger>
            <TabsTrigger
              value="bookings"
              className="rounded-full data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white px-5 data-[state=active]:shadow-none"
              data-testid="tab-bookings"
            >
              Booking Bar ({bookings.length})
            </TabsTrigger>
            <TabsTrigger
              value="reviews"
              className="rounded-full data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white px-5 data-[state=active]:shadow-none"
              data-testid="tab-reviews"
            >
              Reviews ({reviews.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="contacts" className="mt-6">
            <Card className="border-[#E5E0D8] bg-white overflow-hidden">
              {loading ? (
                <div className="py-14 flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-[#C8A97E]" />
                </div>
              ) : contacts.length === 0 ? (
                <EmptyState
                  icon={Inbox}
                  title="No contact enquiries yet"
                  desc="Submissions from the contact form will appear here."
                />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F9F7F3] hover:bg-[#F9F7F3]">
                        <TableHead className="w-[140px]">Received</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Room</TableHead>
                        <TableHead>Basis</TableHead>
                        <TableHead>Check-in</TableHead>
                        <TableHead>Guests</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contacts.map((c) => (
                        <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                          <TableCell className="text-[12px] text-[#666]">
                            {formatDate(c.created_at)}
                          </TableCell>
                          <TableCell className="font-medium text-[#1A1A1A]">{c.name}</TableCell>
                          <TableCell>
                            <a
                              href={`tel:${c.phone}`}
                              className="text-[#C8A97E] hover:underline inline-flex items-center gap-1"
                            >
                              <Phone className="w-3 h-3" /> {c.phone}
                            </a>
                          </TableCell>
                          <TableCell>
                            {c.email ? (
                              <a
                                href={`mailto:${c.email}`}
                                className="text-[#C8A97E] hover:underline inline-flex items-center gap-1 text-[12px]"
                              >
                                <Mail className="w-3 h-3" /> {c.email}
                              </a>
                            ) : (
                              <span className="text-[#9A9A9A]">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-[13px]">{c.room_type || "—"}</TableCell>
                          <TableCell className="text-[13px]">{c.rental_basis || "—"}</TableCell>
                          <TableCell className="text-[13px]">{c.check_in || "—"}</TableCell>
                          <TableCell className="text-[13px]">{c.guests || "—"}</TableCell>
                          <TableCell className="max-w-[240px] truncate text-[13px] text-[#666]">
                            {c.message || "—"}
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={() => deleteContact(c.id)}
                              className="w-8 h-8 rounded-full hover:bg-red-50 text-red-600 flex items-center justify-center transition-colors"
                              data-testid={`delete-contact-${c.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            <Card className="border-[#E5E0D8] bg-white overflow-hidden">
              {loading ? (
                <div className="py-14 flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-[#C8A97E]" />
                </div>
              ) : bookings.length === 0 ? (
                <EmptyState
                  icon={Calendar}
                  title="No quick-booking enquiries yet"
                  desc="Quick booking bar submissions will appear here."
                />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F9F7F3] hover:bg-[#F9F7F3]">
                        <TableHead className="w-[140px]">Received</TableHead>
                        <TableHead>Check-in</TableHead>
                        <TableHead>Check-out</TableHead>
                        <TableHead>Room Type</TableHead>
                        <TableHead>Basis</TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map((b) => (
                        <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                          <TableCell className="text-[12px] text-[#666]">
                            {formatDate(b.created_at)}
                          </TableCell>
                          <TableCell className="text-[13px]">{b.check_in || "—"}</TableCell>
                          <TableCell className="text-[13px]">{b.check_out || "—"}</TableCell>
                          <TableCell className="text-[13px]">{b.room_type || "—"}</TableCell>
                          <TableCell className="text-[13px]">{b.rental_basis || "—"}</TableCell>
                          <TableCell>
                            <button
                              onClick={() => deleteBooking(b.id)}
                              className="w-8 h-8 rounded-full hover:bg-red-50 text-red-600 flex items-center justify-center transition-colors"
                              data-testid={`delete-booking-${b.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <ReviewsAdminTab
              reviews={reviews}
              setReviews={setReviews}
              loading={loading}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const ReviewsAdminTab = ({ reviews, setReviews, loading }) => {
  const [editing, setEditing] = useState(null); // null | {} (new) | review (edit)

  const removeReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.deleteReview(id);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const togglePublished = async (rev) => {
    try {
      const updated = await api.updateReview(rev.id, { published: !rev.published });
      setReviews((prev) => prev.map((r) => (r.id === rev.id ? updated : r)));
      toast.success(updated.published ? "Review published" : "Review hidden");
    } catch {
      toast.error("Could not update");
    }
  };

  const onSaved = (saved, isNew) => {
    setReviews((prev) =>
      isNew ? [saved, ...prev] : prev.map((r) => (r.id === saved.id ? saved : r))
    );
    setEditing(null);
  };

  return (
    <Card className="border-[#E5E0D8] bg-white overflow-hidden">
      <div className="flex items-center justify-between p-5 border-b border-[#E5E0D8]">
        <div>
          <div className="font-serif text-[18px] font-semibold text-[#1A1A1A]">
            Guest Reviews
          </div>
          <div className="text-[12px] text-[#666] mt-0.5">
            Add, edit, hide or delete testimonials. Only published reviews show on the website.
          </div>
        </div>
        <Button
          onClick={() => setEditing({})}
          className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white rounded-full h-10 px-5 font-semibold"
          data-testid="add-review-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add Review
        </Button>
      </div>

      {loading ? (
        <div className="py-14 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#C8A97E]" />
        </div>
      ) : reviews.length === 0 ? (
        <EmptyState
          icon={Star}
          title="No reviews yet"
          desc="Add your first guest testimonial to build trust on your website."
        />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9F7F3] hover:bg-[#F9F7F3]">
                <TableHead className="w-[60px]">Status</TableHead>
                <TableHead>Guest</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead>Quote</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="w-[140px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reviews.map((r) => (
                <TableRow key={r.id} data-testid={`review-row-${r.id}`}>
                  <TableCell>
                    <button
                      onClick={() => togglePublished(r)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        r.published
                          ? "bg-green-50 text-green-700 hover:bg-green-100"
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      }`}
                      data-testid={`toggle-pub-${r.id}`}
                      title={r.published ? "Click to hide" : "Click to publish"}
                    >
                      {r.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    </button>
                  </TableCell>
                  <TableCell className="font-medium text-[#1A1A1A]">{r.name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          className={`w-3 h-3 ${
                            i <= r.rating
                              ? "fill-[#C8A97E] text-[#C8A97E]"
                              : "text-[#E5D9BF]"
                          }`}
                        />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[360px] truncate text-[13px] text-[#666] italic">
                    "{r.quote}"
                  </TableCell>
                  <TableCell className="text-[13px]">{r.location || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => setEditing(r)}
                        className="w-8 h-8 rounded-full hover:bg-[#FAF3E7] text-[#C8A97E] flex items-center justify-center transition-colors"
                        data-testid={`edit-review-${r.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => removeReview(r.id)}
                        className="w-8 h-8 rounded-full hover:bg-red-50 text-red-600 flex items-center justify-center transition-colors"
                        data-testid={`delete-review-${r.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ReviewDialog
        open={editing !== null}
        review={editing}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
      />
    </Card>
  );
};

const ReviewDialog = ({ open, review, onClose, onSaved }) => {
  const isNew = review && !review.id;
  const [form, setForm] = useState({
    name: "",
    rating: 5,
    location: "",
    quote: "",
    photo_url: "",
    published: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (review) {
      setForm({
        name: review.name || "",
        rating: review.rating || 5,
        location: review.location || "",
        quote: review.quote || "",
        photo_url: review.photo_url || "",
        published: review.published !== false,
      });
    }
  }, [review]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.quote) {
      toast.error("Name and quote are required");
      return;
    }
    setSaving(true);
    try {
      const saved = isNew
        ? await api.createReview(form)
        : await api.updateReview(review.id, form);
      toast.success(isNew ? "Review added" : "Review updated");
      onSaved(saved, isNew);
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg bg-white" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-[24px] text-[#1A1A1A]">
            {isNew ? "Add new review" : "Edit review"}
          </DialogTitle>
          <DialogDescription className="text-[#666]">
            {isNew
              ? "Add a guest testimonial to display on your website."
              : "Update this review."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] font-semibold">
              Guest Name
            </label>
            <Input
              value={form.name}
              onChange={(e) => upd("name", e.target.value)}
              placeholder="e.g. Rajesh Kumar"
              className="mt-1.5 bg-[#F9F7F3] border-[#E5E0D8] focus-visible:border-[#C8A97E] focus-visible:ring-[#C8A97E]/20"
              data-testid="review-name-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] font-semibold">
                Rating
              </label>
              <div className="mt-2 flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => upd("rating", i)}
                    data-testid={`star-${i}`}
                  >
                    <Star
                      className={`w-6 h-6 ${
                        i <= form.rating
                          ? "fill-[#C8A97E] text-[#C8A97E]"
                          : "text-[#E5D9BF]"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] font-semibold">
                Location (optional)
              </label>
              <Input
                value={form.location}
                onChange={(e) => upd("location", e.target.value)}
                placeholder="e.g. Hyderabad"
                className="mt-1.5 bg-[#F9F7F3] border-[#E5E0D8] focus-visible:border-[#C8A97E] focus-visible:ring-[#C8A97E]/20"
                data-testid="review-location-input"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] font-semibold">
              Review Quote
            </label>
            <Textarea
              value={form.quote}
              onChange={(e) => upd("quote", e.target.value)}
              placeholder="What did the guest say about their stay?"
              rows={4}
              className="mt-1.5 bg-[#F9F7F3] border-[#E5E0D8] focus-visible:border-[#C8A97E] focus-visible:ring-[#C8A97E]/20"
              data-testid="review-quote-input"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#9A9A9A] font-semibold">
              Photo URL (optional)
            </label>
            <Input
              value={form.photo_url}
              onChange={(e) => upd("photo_url", e.target.value)}
              placeholder="https://i.pravatar.cc/150?img=12 or any image URL"
              className="mt-1.5 bg-[#F9F7F3] border-[#E5E0D8] focus-visible:border-[#C8A97E] focus-visible:ring-[#C8A97E]/20"
              data-testid="review-photo-input"
            />
            <div className="text-[10px] text-[#9A9A9A] mt-1">
              Tip: leave blank to show initials. For sample avatars use{" "}
              <code className="bg-[#FAF3E7] px-1 rounded">https://i.pravatar.cc/150?img=N</code> (N = 1–70).
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-[#E5E0D8]">
            <div>
              <div className="text-[13px] font-semibold text-[#1A1A1A]">Published</div>
              <div className="text-[11px] text-[#9A9A9A]">
                Show this review on the website
              </div>
            </div>
            <Switch
              checked={form.published}
              onCheckedChange={(v) => upd("published", v)}
              data-testid="review-published-switch"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-full border-[#E5E0D8]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white rounded-full font-semibold"
              data-testid="review-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Review"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const StatBox = ({ icon: Icon, label, count }) => (
  <Card className="p-5 bg-white border-[#E5E0D8] flex items-center gap-4">
    <div className="w-11 h-11 rounded-[12px] bg-[#FAF3E7] flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#C8A97E]" strokeWidth={1.5} />
    </div>
    <div>
      <div className="font-serif text-[26px] font-semibold text-[#1A1A1A] leading-none">
        {count}
      </div>
      <div className="text-[11px] tracking-[0.15em] uppercase text-[#9A9A9A] font-semibold mt-1">
        {label}
      </div>
    </div>
  </Card>
);

const EmptyState = ({ icon: Icon, title, desc }) => (
  <div className="py-16 text-center">
    <div className="w-14 h-14 rounded-full bg-[#F9F7F3] flex items-center justify-center mx-auto mb-4">
      <Icon className="w-5 h-5 text-[#9A9A9A]" />
    </div>
    <div className="font-serif text-[18px] font-semibold text-[#1A1A1A] mb-1">{title}</div>
    <div className="text-[13px] text-[#666]">{desc}</div>
  </div>
);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default Admin;
